<script lang="ts">
export const UINT_PATTERN = '(0|([1-9][0-9]*))';
export const UINT_PATTERN_NO_ZERO = '[1-9][0-9]*';
export const INT_PATTERN = '-?(0|([1-9][0-9]*))';
export const FLOAT_PATTERN = '-?(0|([1-9][0-9]*))([.,][0-9]*)?';
</script>

<script setup lang="ts">
import {
  computed,
  inject,
  useAttrs,
  useTemplateRef,
  type InputHTMLAttributes,
  type StyleValue,
} from 'vue';
import { NxValidatedTextInput } from '.';
import { i18nInjectionKey, type AlignedTextInputProps } from './internal';

defineOptions({ inheritAttrs: false });

// `align` is handed on with the rest of `boundAttrs`.
const props = defineProps<
  AlignedTextInputProps & {
    pattern: string;
    onInput?: (payload: InputEvent) => void;
    /**
     * `ribbon` is the office ribbon's control: the ribbon's height, type and
     * border (`--office-ribbon-*`), the surface fill. Size it with
     * `wrapperStyle` (a ribbon field is as wide as its numbers, not the row).
     */
    size?: 'field' | 'ribbon';
    /**
     * Opt-in spin button: `role="spinbutton"` with its `aria-value*` from the
     * `min` / `max` attributes, and ArrowUp / ArrowDown step by `step`
     * (default 1) within that range — firing `input` and `change` like a typed
     * value. Without it `min` / `max` / `step` stay plain attributes and the
     * arrow keys are left to the surroundings (a table's cell navigation).
     */
    spinButton?: boolean;
    /**
     * At most this many decimals (the precision). The field shows the value
     * rounded to it (`1,2345` at 4), and with it set a typed or stepped value
     * is handed on rounded the same way, as an exact decimal string — no
     * float drift (`"0.3"`, never `0.30000000000000004`). Without it: three
     * decimals shown, the typed text handed on as it is (the old behaviour).
     */
    maxFractionDigits?: number;
    /**
     * At least this many decimals shown (`12,50` at 2). Display only — the
     * handed-on value keeps no trailing zeros. Pads while the field is
     * re-rendered, so use it on a `lazy` field.
     */
    minFractionDigits?: number;
    /**
     * The language the number is shown in (BCP-47, `de`, `en-US`) — its
     * decimal separator. Takes precedence over the kit i18n; for a host
     * without it (a standalone package such as `@blpde/calc`) that would
     * otherwise fall back to the browser's language. Typing accepts `,` and
     * `.` as the decimal separator either way. Without it: the kit i18n's
     * language, else the browser's.
     */
    locale?: string;
  } & /* @vue-ignore */ Omit<
      InputHTMLAttributes,
      'pattern' | 'onInput' | 'size' | 'min' | 'max' | 'step'
    >
>();

const attrs = useAttrs();

// Optional: an embedder without the kit's i18n (Write in a foreign host)
// formats with the `locale` prop, else the browser's locale.
const i18nInjection = inject(i18nInjectionKey, null);
const locale = computed(() => props.locale || i18nInjection?.locale.value);

/** Fraction digits for Intl; empty without a precision (Intl's default: 0–3). */
const fractionDigits = computed<Intl.NumberFormatOptions>(() => {
  const min = props.minFractionDigits;
  const max = props.maxFractionDigits;
  if (min === undefined && max === undefined) return {};
  const minimumFractionDigits = min ?? 0;
  return {
    minimumFractionDigits,
    maximumFractionDigits: Math.max(minimumFractionDigits, max ?? 3),
    // `-0.00001` at four decimals is `0`, not `-0`.
    signDisplay: 'negative',
  };
});

const nrFormat = computed(() => {
  return new Intl.NumberFormat(locale.value, {
    useGrouping: false,
    ...fractionDigits.value,
  });
});

/**
 * The handed-on value at the precision: a plain decimal string, rounded as
 * the field shows it. Intl formats a decimal string exactly (no detour
 * through a float), so `"1.00005"` rounds to `"1.0001"`.
 */
const exactFormat = computed(() => {
  const max = props.maxFractionDigits;
  if (max === undefined) return null;
  return new Intl.NumberFormat('en-US', {
    useGrouping: false,
    maximumFractionDigits: Math.max(props.minFractionDigits ?? 0, max),
    signDisplay: 'negative',
  });
});

/** A decimal string as Intl takes it, so it is formatted exactly. */
const decimal = (value: string) => value.trim() as Intl.StringNumericLiteral;

/** Value will be `NaN` if the Input is empty or if the number-conversion fails */
const [modelValue, modifier] = defineModel<number | string, 'lazy', string, string>({
  required: true,
  get(value) {
    if (typeof value === 'number') {
      return nrFormat.value.format(value);
    }

    return value ? nrFormat.value.format(decimal(value)) : '';
  },
  set(value) {
    const text = value.replace(',', '.');
    const exact = exactFormat.value;
    // Only a readable number is rounded; anything else is handed on as typed.
    return exact && parseNumber(text) !== undefined ? exact.format(decimal(text)) : text;
  },
});

/** An HTML attribute value (`min="1"` or `:min="1"`) as a number, if it is one. */
function numericAttr(value: unknown): number | undefined {
  if (value === undefined || value === null || value === '') return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}
const spin = computed(() => props.spinButton === true);
const min = computed(() => numericAttr(attrs.min));
const max = computed(() => numericAttr(attrs.max));
const step = computed(() => numericAttr(attrs.step) ?? 1);
/** A field text (`3`, `1,5`) as a number, if it is one. */
function parseNumber(text: unknown): number | undefined {
  const raw = String(text ?? '').replace(',', '.');
  const value = Number(raw);
  return raw !== '' && Number.isFinite(value) ? value : undefined;
}
const currentNumber = computed(() => parseNumber(modelValue.value));

function clamp(value: number): number {
  let next = value;
  if (min.value !== undefined) next = Math.max(min.value, next);
  if (max.value !== undefined) next = Math.min(max.value, next);
  return next;
}

function onSpinKeydown(event: KeyboardEvent): void {
  if (!spin.value || (event.key !== 'ArrowUp' && event.key !== 'ArrowDown')) return;
  if (event.isComposing || event.keyCode === 229) return;
  const input = event.target;
  if (!(input instanceof HTMLInputElement)) return;
  event.preventDefault();
  // Step from what the field shows, not from the model: with a lazy model a
  // typed `10` is not committed yet, and ArrowUp has to give `11`.
  const shown = parseNumber(input.value);
  const base = shown ?? currentNumber.value ?? min.value ?? 0;
  const next = clamp(base + (event.key === 'ArrowUp' ? step.value : -step.value));
  if (next === shown) return;
  // The same events a native number field fires, so `@input`/`@change`
  // listeners and the model all see the step as if it had been typed.
  input.value = nrFormat.value.format(next);
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

// A ribbon field is as wide as the consumer's wrapperStyle says, not the row.
const wrapperStyle = computed<StyleValue>(() =>
  props.size === 'ribbon' ? [{ width: 'auto' }, props.wrapperStyle] : props.wrapperStyle
);

const boundAttrs = computed<Record<string, unknown>>(() => {
  const {
    size: _size,
    spinButton: _spinButton,
    maxFractionDigits: _maxFractionDigits,
    minFractionDigits: _minFractionDigits,
    locale: _locale,
    // The model goes through v-model below, never as attributes.
    modelValue: _modelValue,
    modelModifiers: _modelModifiers,
    pattern: _pattern,
    wrapperStyle: _wrapperStyle,
    ...rest
  } = { ...props, ...attrs } as Record<string, unknown>;
  const consumerKeydown = attrs.onKeydown as ((event: KeyboardEvent) => void) | undefined;
  return {
    ...rest,
    class: [attrs.class, { 'size-ribbon': props.size === 'ribbon' }],
    ...(spin.value
      ? {
          role: 'spinbutton',
          inputmode: 'numeric',
          'aria-valuemin': min.value,
          'aria-valuemax': max.value,
          'aria-valuenow': currentNumber.value,
        }
      : {}),
    onKeydown: (event: KeyboardEvent) => {
      onSpinKeydown(event);
      consumerKeydown?.(event);
    },
  };
});

const inputRef = useTemplateRef('nx-validated-input-element');
const exposeRef = computed(() => {
  return inputRef.value?.inputElement;
});
defineExpose({
  inputElement: exposeRef,
});
</script>

<template>
  <NxValidatedTextInput
    v-if="modifier.lazy"
    v-bind="boundAttrs"
    ref="nx-validated-input-element"
    v-model.lazy="modelValue"
    :pattern="props.pattern"
    :wrapper-style="wrapperStyle"
  ></NxValidatedTextInput>
  <NxValidatedTextInput
    v-else
    v-bind="boundAttrs"
    ref="nx-validated-input-element"
    v-model="modelValue"
    :pattern="props.pattern"
    :wrapper-style="wrapperStyle"
  ></NxValidatedTextInput>
</template>
